/**
 * #650 — the trader's own resting orders at one ladder level, bounded.
 *
 * Every ladder drew one chip per own order in an unbounded horizontal flex
 * row. Measured on the real board at 1470x956, one rung's chip group ran
 * **64px at 1 order → 141 → 339 → 537px at 10**, and it grows by TAKING:
 * `TwoSidedLadder`'s price column is `minmax(0,1fr)`, so it shrinks to make
 * room. The price text's right edge walked 500px → 423px → 225px → **27px**
 * across that ramp, and at the top of it every chip lost its queue reading to
 * its own `truncate` while the odds and resting size overprinted each other.
 * Nothing on screen said any of it had happened.
 *
 * So the group is now bounded: at most `INLINE_CHIP_LIMIT` chips inline, and
 * when there are more, a trailing `+N` opens a popover listing every order —
 * each one still its own chip, still its own cancel.
 *
 * ─── Why a fixed count, not "as many as fit" ─────────────────────────────────
 *
 * Fitting is the thing the ticket describes, but measuring it means reading
 * layout during render and re-deciding on every resize and every quote tick —
 * on a surface whose whole complaint is that it moves. A fixed cap is decided
 * before layout, so the group's width depends only on the count, and the count
 * only changes when orders actually arrive or leave. The cap is 2 because 2 is
 * the widest the group gets while the row still renders cleanly at the
 * narrowest supported viewport (141px of chips, price column still 423px); by
 * 5 the price column is down to 225px.
 *
 * ⚠️ **A level is a PROP, and it changes under a mounted component.** The two
 * table ladders key their rows by INDEX — `GameTable` by
 * `${game.key}-${teamSide}-${kind}-${idx}` and `SpreadsTotalsTable` by
 * `ask-${idx}` / `bid-${idx}` — so one new best bid, routine, hands this same
 * instance a different price. An open popover that survived that would be
 * listing, and offering to cancel, orders from a rung the row no longer draws.
 * (`TwoSidedLadder` keys by `${kind}-${cents}` and so remounts instead; the
 * guard below must still hold there, because that is its row key's doing and
 * not this component's.)
 * The open state is therefore dropped the moment the level identity changes,
 * using the same adjust-during-render pattern `CancelLevelControl` documents
 * (an effect runs a tick late, leaving one frame of a stale-but-clickable
 * list). This is the single most important thing in this file.
 *
 * ─── #676 — WHICH two, not how many ────────────────────────────────────────
 *
 * The bound above says how many chips a rung may draw; it said nothing about
 * which ones, so the buckets arrived sorted largest-remaining-first and the
 * bound kept the biggest orders rather than the most actionable. Traders hit
 * the consequence on GBuff: the order about to fill sat inside `+N` while one
 * with thousands of contracts ahead of it held an inline slot.
 *
 * The rank (`rankRestingOrders`, in `helpers.ts`) lives with the truncation
 * rather than in the four builders that fill these buckets — whoever cuts the
 * list decides what survives the cut, and a caller cannot forget a rule it
 * never has to apply. The compact band in `CompactOutcomeBands` is the other
 * truncating surface and applies the same rank before its own slice.
 *
 * Nothing about a chip's presentation moves: a retained reading keeps its
 * "last confirmed Ns ago" tooltip, an unknown one still reads `?`, and both
 * simply rank after every confirmed position instead of being reworded.
 *
 * ─── #677 — COMPACT vs EXPANDED, and why that is not a rollback ────────────
 *
 * `displaySettings.restingOrderDisplay` chooses between two BOUNDED
 * presentations of the same ranked list. Compact is everything described
 * above. Expanded draws every order at the level in one region that is capped
 * in width (so the price column is untouchable), wraps instead of widening,
 * and scrolls instead of growing the rung without limit.
 *
 * The distinction that matters: the thing traders asked for — "let me see all
 * of them" — is served, while the thing that broke the ladder — a group whose
 * width grows with the order count — is not reachable from either setting. The
 * pre-#650 flex row is not the expanded mode; it is not an option at all.
 *
 * Expanded has no `+N`, so the guard that closes an open popover has a third
 * trigger (the mode itself) alongside a changed rung and a drained level.
 *
 * `CompactOutcomeBands` is deliberately NOT wired to this preference: it has
 * no per-order cancel, and expanded's contract is that every order stays
 * individually cancellable. Widening cancel affordance there is a trading
 * change, not a presentation one. See the #677 audit matrix, record 4.6.
 *
 * Keyboard: the chips were `<span role="button">` with an `onClick` and nothing
 * else — not focusable, not operable. They are real `<button>`s now, and that
 * is the SAFETY FENCE rather than a style choice: `Lt2HotkeyListener` decides
 * whether a keystroke belongs to a control via `el.closest('button')` and
 * deliberately does not look at `role="button"`, so a focusable span would let
 * a trader tabbed onto a CANCEL control press A/D and place an order from it.
 * `RestingOrderChip` documents the fence at the element itself; this paragraph
 * used to claim the opposite (that the chips stay spans on purpose) and was
 * simply wrong about the file it sits beside.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/infra/utils';
import { useAccountStore } from '@/stores/accountStore';
import { INLINE_CHIP_LIMIT, useDisplaySettings } from '@/stores/displaySettings';
import { isKalshiCancelPending } from '@/lib/trading/kalshiCancelLifecycle';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';
import { RestingOrderChip } from './RestingOrderChip';
import { CancelLevelControl, type LadderLevelIdentity } from './CancelLevelControl';
import {
  QUEUE_ROW_ORDER_NOTE,
  rankRestingOrders,
  unconfirmedQueueCount,
  unconfirmedQueueNote,
  type BidRowOrder,
} from './helpers';

/**
 * How many chips render inline before the rest move into the popover.
 *
 * Raising this is one line, and it is the knob to turn if the desk would
 * rather see more at a glance — but it is bounded by the narrowest supported
 * viewport, not by taste: see the measurements in the file header.
 *
 * #677 moved the DEFINITION to `displaySettings`, beside the preference it
 * parameterizes, because the Settings row and the status-bar mirror now put
 * this number in front of traders in words. Re-exported here so this file
 * stays the place you look for it, and so the measurements above stay the
 * thing you read before changing it.
 */
export { INLINE_CHIP_LIMIT };

/**
 * #677 — the EXPANDED region's bound, and the reason expanded is not the
 * pre-#650 row wearing a new name.
 *
 * Compact's width varies with the count (64px at one order, 141px at two).
 * Expanded's cannot: it takes what it needs up to `EXPANDED_MAX_WIDTH` and
 * then WRAPS, so the group's contribution to the row is capped no matter how
 * many orders rest at the level. The cap is the popover's width, deliberately
 * — the popover is the widest own-order surface the ladder already tolerates,
 * so nothing here is wider than something already shipped. At 1470×956 that
 * leaves the price column ~27px narrower than the two-chip case and ~170px
 * wider than five inline chips ever left it.
 *
 * `max-width`, not `width`: a rung with one order must not reserve 168px it
 * has no use for. In a table cell max-width also caps the cell's max-content
 * contribution, which is what stops the column widening — the #650 failure.
 *
 * Height is capped too, because "contained" has to mean both directions: past
 * `EXPANDED_MAX_ROWS` the region scrolls inside itself rather than growing the
 * rung without limit. Chips are `leading-4` (16px) with a 2px row gap.
 */
const EXPANDED_MAX_WIDTH = 168;
const EXPANDED_MAX_ROWS = 3;
const EXPANDED_ROW_HEIGHT = 18; // 16px chip + 2px gap
/** The count heading, which is part of the box rather than floating above it. */
const EXPANDED_HEADING_HEIGHT = 14;
const EXPANDED_MAX_HEIGHT = EXPANDED_HEADING_HEIGHT + EXPANDED_MAX_ROWS * EXPANDED_ROW_HEIGHT;

/** Panel geometry. Fixed width so the position can be computed before layout
 *  rather than measured after it — the ladder repaints ~4x/s. */
const PANEL_WIDTH = EXPANDED_MAX_WIDTH;
const VIEWPORT_MARGIN = 8;
const MIN_PANEL_HEIGHT = 80;
/** Content geometry, so the panel can be PLACED before it is measured: the
 *  ladder repaints ~4x/s and a measure-then-move would be visible as a jump. */
const PANEL_CHROME = 28; // padding + the "N resting @ X¢" heading
const PANEL_ROW = 20; // one chip row at the pinned leading-4

/**
 * Everything that distinguishes one rung from another, as one value. Equality
 * of this string is what "the row still means what it meant" means.
 *
 * `priceCents` is folded in separately because a surface may supply the price
 * WITHOUT a level (TwoSidedLadder passes `level={null}`). Keyed on the level
 * alone this returned `''` for every rung there, so the stale-rung guard was
 * inert on that surface and only its `${kind}-${cents}` row key kept it safe —
 * safety by accident, which the next index-keyed caller would not inherit.
 */
function rungKey(level: LadderLevelIdentity | null, priceCents: number | null): string {
  if (level) {
    return `${level.marketTicker}|${level.side}|${level.action}|${level.priceCents}`;
  }
  return priceCents != null ? `|||${priceCents}` : '';
}

/** The hairline between two chips. Shared so the inline row and the popover
 *  cannot drift into two different separators for the same relationship. */
function ChipSeparator() {
  return <span className="bg-muted-foreground/40 h-3 w-px shrink-0 self-center" />;
}

export function RestingOrderGroup({
  orders,
  level,
  priceCents,
  inlineLimit = INLINE_CHIP_LIMIT,
  display = 'preference',
  compactLevelCancel = false,
  chipSlotPx,
}: {
  orders: readonly BidRowOrder[];
  /** Null on surfaces that do not offer the level-wide cancel. */
  level: LadderLevelIdentity | null;
  /**
   * The rung's price, for surfaces that have one but no level-cancel control.
   * Separate from `level` on purpose: the contract forbids truncating the
   * price-level ASSOCIATION, and a panel headed only "10 resting" has lost it
   * — but offering a destructive level-wide cancel where none existed before
   * is not this ticket's to add, so the two cannot be the same prop.
   */
  priceCents?: number | null;
  /**
   * #884 — how many chips this SURFACE draws inline before the `+N`
   * disclosure; defaults to the shared `INLINE_CHIP_LIMIT`, so every existing
   * caller is untouched. The Butterfly's narrow rung passes 0: the count is
   * the whole inline affordance there and the popover carries every chip,
   * with its cancel and queue reading, exactly as it does behind `+N` today.
   */
  inlineLimit?: number;
  /**
   * #884 — `'count'` is an explicit presentation override: the count
   * disclosure regardless of the trader's #677 chip preference. A rung too
   * narrow for a chip cannot host the expanded region either — in that mode
   * a first order would allocate the region's fixed height inside the rung
   * and move every price below it, which is the one thing the narrow book
   * exists to prevent. Default `'preference'` honors the setting as every
   * other ladder does.
   */
  display?: 'preference' | 'count';
  /**
   * #1070 — the level cancel confirms in its bounded overlay
   * (`CancelLevelControl compact`) instead of the wider inline face. For a
   * fixed-width host: the inline `✕ 3 · 6000000?` face would squeeze the
   * chips beside it until their quantities and queues were unreadable.
   */
  compactLevelCancel?: boolean;
  /**
   * #1070 — each inline chip sits in a right-aligned slot this wide, so a
   * chip whose text changes width (a quantity crossing a digit, arming into
   * `✕ N?`) moves only its own left edge, never its neighbours or `+N`. The
   * slot may shrink; the chip keeps its natural width and hit area. A chip
   * wider than its shrunk slot is clipped at its right edge (`safe` alignment
   * keeps its start in view) instead of ellipsized, so it loses pixels of its
   * last digit, never its whole queue reading. `overflow-x: clip` leaves the
   * vertical axis visible, so the chip's focus ring is clipped no more than
   * the gutter already clips it.
   */
  chipSlotPx?: number;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // The whole map, as CancelLevelControl reads it: this control cares about N
  // orders at once, and every lifecycle transition returns the map BY IDENTITY
  // when nothing changed, so a quiet poll cannot re-render the ladder.
  const cancelStates = useAccountStore((s) => s.kalshiCancelStates);
  // #677 — read reactively, so flipping the preference re-lays the live rungs
  // rather than waiting for a remount the ladder may never do. A `'count'`
  // surface (#884) never expands, whatever the preference says.
  const preferExpanded = useDisplaySettings((s) => s.restingOrderDisplay) === 'expanded';
  const expanded = display === 'count' ? false : preferExpanded;

  const rungCents = level?.priceCents ?? priceCents ?? null;
  const priceLabel = rungCents != null ? `${rungCents}¢` : '';

  const key = rungKey(level, rungCents);
  const [seenKey, setSeenKey] = useState(key);
  // React's adjust-state-during-render pattern, NOT an effect — see the header.
  if (seenKey !== key) {
    setSeenKey(key);
    if (open) setOpen(false);
  }

  // ⚠️ An open panel must not survive its own disclosure disappearing.
  // Orders fill and cancel constantly: a trader who opens `+8`, watches the
  // level drain below the inline cap, then places more would otherwise have
  // the panel spring back open on its own — an unrequested overlay of cancel
  // controls over a live book. Held here rather than in an effect for the same
  // reason as the rung change: an effect leaves one frame in which the stale
  // panel is on screen AND clickable.
  const overflowCount = Math.max(0, orders.length - inlineLimit);
  if (open && overflowCount === 0) setOpen(false);
  // …and the same holds when the DISCLOSURE ITSELF goes away. Switching to
  // expanded removes the `+N` trigger, so an open panel would be left floating
  // over the board with nothing on screen that opened it and no trigger to
  // return focus to. Dropped during render for the same reason as the two
  // guards above: an effect leaves one frame of an orphaned, clickable panel.
  if (open && expanded) setOpen(false);

  /**
   * ⚠️ The panel is PORTALLED, and that is not cosmetic.
   *
   * Both table ladders wrap this group in `overflow-hidden` (GameTable and
   * SpreadsTotalsTable — it is what stops the own-order column from widening
   * the table). An absolutely-positioned panel inside that wrapper is clipped
   * by it, so the expanded list could be cut off exactly where a trader needs
   * to read it. Rendering to `document.body` escapes every clipping ancestor
   * without touching the wrapper that the column bound depends on.
   *
   * `fixed` + explicit coordinates rather than `absolute`, for the same reason
   * `AlertControlsMenu` does it: the ladder lives inside a horizontally
   * scrolling container, so the panel has to be re-anchored on scroll instead
   * of riding a positioned ancestor.
   */
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 0 });
  /** How many rows the panel will draw — the overflow, not the whole set. */
  const hiddenRows = Math.max(0, orders.length - inlineLimit);

  const reposition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Right-align to the trigger, then clamp so it can never leave the viewport
    // on a narrow board — the failure this whole ticket is about.
    const rightLimit = Math.max(
      VIEWPORT_MARGIN,
      window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN,
    );
    const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.right - PANEL_WIDTH), rightLimit);

    // Ladder rungs sit low on the board as often as not, so "always below" put
    // the tail of the list under the fold — visible only by scrolling, which
    // is not the same as visible. Prefer below, FLIP ABOVE when the whole list
    // fits there and would not fit below, and only clamp-and-scroll when it
    // fits in neither.
    const wanted = PANEL_CHROME + hiddenRows * PANEL_ROW;
    const roomBelow = window.innerHeight - rect.bottom - 4 - VIEWPORT_MARGIN;
    const roomAbove = rect.top - 4 - VIEWPORT_MARGIN;

    let top: number;
    if (wanted <= roomBelow || roomBelow >= roomAbove) {
      top = Math.max(VIEWPORT_MARGIN, rect.bottom + 4);
    } else {
      top = Math.max(VIEWPORT_MARGIN, rect.top - 4 - Math.min(wanted, roomAbove));
    }
    if (window.innerHeight - top - VIEWPORT_MARGIN < MIN_PANEL_HEIGHT) {
      top = Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - MIN_PANEL_HEIGHT);
    }
    const maxHeight = window.innerHeight - top - VIEWPORT_MARGIN;
    setPos((prev) =>
      prev.top === top && prev.left === left && prev.maxHeight === maxHeight
        ? prev
        : { top, left, maxHeight },
    );
  }, [hiddenRows]);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    // Capture phase: the scrolling element is the table's own overflow-x
    // container, not the window.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  /** Close, and put focus somewhere real. A keyboard user who pressed Escape
   *  while inside the panel would otherwise be dropped on <body> — the panel
   *  that held their focus unmounts — losing their place in the ladder
   *  entirely. Only reclaimed when focus was actually inside; a mouse dismiss
   *  elsewhere on the page must not yank focus back here. */
  const close = () => {
    const inside = panelRef.current?.contains(document.activeElement);
    setOpen(false);
    if (inside) triggerRef.current?.focus();
  };

  useDismissibleLayer({
    open,
    onDismiss: close,
    refs: [triggerRef, panelRef],
    // Matches AlertControlsMenu: this panel is `z-[100]` while atoms/Modal is
    // `z-50`, so without focus-out dismissal it could sit ON TOP of a modal as
    // a background layer. Focus inside the panel counts as inside (both the
    // trigger and the panel are refs), so tabbing between chips is safe.
    dismissOnFocusOut: true,
  });

  // The portal appends the panel at the END of <body>, so a Tab from the
  // trigger would otherwise walk the whole rest of the page before reaching
  // the orders — which would make "every order reachable by keyboard" true
  // only in the most literal sense. Move focus into the panel on open; Escape
  // hands it back to the trigger via `close()`.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (orders.length === 0) return null;

  // #676 — most actionable first, so the bound keeps the orders nearest the
  // front of the queue. The panel reads in the same order as the row for the
  // same reason the row is ordered at all: a trader scanning both should not
  // have to hold two arrangements of one level in their head.
  const ranked = rankRestingOrders(orders);
  const inline = ranked.slice(0, inlineLimit);
  const hidden = ranked.slice(inlineLimit);
  const hiddenCount = overflowCount;

  /**
   * A hidden order in a non-neutral lifecycle state is the one thing a bare
   * count cannot say. The contract forbids truncating pending/timed-out/error
   * state without a visible affordance, so the disclosure carries it: amber
   * for a cancel the venue never confirmed (the trader must act), dimmed-pulse
   * for one still in flight. The chips inside keep their own exact states.
   */
  const hiddenTimedOut = hidden.some((o) => cancelStates[o.orderId]?.phase === 'timed-out');
  const hiddenPending = hidden.some((o) => isKalshiCancelPending(cancelStates[o.orderId]));
  const stateNote = hiddenTimedOut
    ? ' — includes a cancel the venue has not confirmed'
    : hiddenPending
      ? ' — includes a cancel in flight'
      : '';

  /**
   * #676 — the disclosure states the ordering rule, because one honest
   * arrangement of it reads as a bug: a SMALLER queue number sitting inside
   * `+N` while a larger one holds an inline slot, which is what happens when
   * the smaller is only a remembered (retained) value and the larger was
   * confirmed this second. Said in the tooltip and the panel's name, where it
   * costs no width on a row whose whole history is about width — and said in
   * #666's words (`unconfirmedQueueNote`), which the compact band shares, so
   * one state cannot pick up two names across two surfaces.
   */
  const queueNote =
    `. ${QUEUE_ROW_ORDER_NOTE}; the panel lists nearest first` +
    unconfirmedQueueNote(unconfirmedQueueCount(hidden), 'hidden');

  /**
   * #677 EXPANDED — every order at the level, in one contained region.
   *
   * What makes this legitimate rather than a rollback of #650 is that the
   * region is bounded in both axes BEFORE layout: `EXPANDED_MAX_WIDTH` caps
   * what the group can contribute to the row (so the price column cannot be
   * taken), `flex-wrap` turns extra orders into extra ROWS instead of extra
   * width, and `EXPANDED_MAX_HEIGHT` turns extra rows into a scroll instead of
   * an ever-taller rung. Ten orders and two orders cost the row the same width.
   *
   * The honest cost, so nobody has to discover it: past three rows the trader
   * scrolls inside a small region. "Every order exposed" here means every
   * order is rendered, reachable and cancellable — not that every order is
   * simultaneously on screen at any rung depth. The chips are real
   * `<button>`s, so tabbing walks them and the browser scrolls each into view.
   *
   * Same `ranked` list as compact, so the two modes cannot disagree about
   * which order comes first, and every chip keeps its own lifecycle state and
   * its own cancel — there is no `+N` here to carry a summary state, because
   * nothing is summarized.
   */
  if (expanded) {
    /**
     * The two jobs `+N` was doing, which expanded would otherwise drop with it.
     *
     * Compact hides orders behind a disclosure, so the disclosure carries both
     * the COUNT (that further orders exist) and their STATE (that one of them
     * needs the trader). Expanded has no disclosure — but it still has a fold:
     * the region scrolls past `EXPANDED_MAX_ROWS`, and on any platform with
     * overlay scrollbars a box showing six chips looks exactly like a box
     * holding six. So the region itself takes both jobs over, computed across
     * EVERY chip rather than only the hidden ones, because which chips are
     * below the fold depends on wrap and cannot be known here.
     *
     * The redundancy when the affected chip is already visible is deliberate
     * and cheap; the alternative is a signal that is correct only when it is
     * not needed.
     */
    const anyTimedOut = ranked.some((o) => cancelStates[o.orderId]?.phase === 'timed-out');
    const anyPending = ranked.some((o) => isKalshiCancelPending(cancelStates[o.orderId]));

    /**
     * Height is FIXED once the level holds more than compact would draw.
     *
     * `max-height` alone let a rung grow and shrink as orders arrived and
     * cancelled — on the exact surface where #650 hunted down a 4px height
     * wobble, and where rows shifting under a trader aiming at a cancel button
     * is a mis-click, not a cosmetic annoyance. Past the compact count the box
     * reserves its full height and stops moving; at or below it the rung keeps
     * its natural size, so a ladder of single-order rungs is not padded to six
     * rows for nothing. The one jump left is at the boundary itself.
     */
    const stableHeight = orders.length > inlineLimit;

    return (
      <>
        {level && <CancelLevelControl level={level} orders={orders} compact={compactLevelCancel} />}
        <div
          role="group"
          aria-label={
            `Your ${orders.length} resting ${orders.length === 1 ? 'order' : 'orders'}` +
            `${priceLabel ? ` at ${priceLabel}` : ' at this level'}` +
            ', nearest the front of the queue first' +
            unconfirmedQueueNote(unconfirmedQueueCount(ranked), 'listed') +
            (anyTimedOut
              ? '. Includes a cancel the venue has not confirmed'
              : anyPending
                ? '. Includes a cancel in flight'
                : '')
          }
          style={{
            maxWidth: EXPANDED_MAX_WIDTH,
            ...(stableHeight
              ? { height: EXPANDED_MAX_HEIGHT }
              : { maxHeight: EXPANDED_MAX_HEIGHT }),
          }}
          className={cn(
            'flex flex-col items-end rounded',
            // The state signal, in the same vocabulary the `+N` disclosure uses
            // on the compact path — amber when the trader must act, a dimmed
            // pulse while a cancel is still in flight.
            anyTimedOut && 'ring-1 ring-amber-400/70',
            !anyTimedOut && anyPending && 'animate-pulse',
          )}
        >
          {/* The count, so a fold can never hide the EXISTENCE of an order.
              Six chips and a heading reading 9 is the whole affordance; a
              scrollbar is not one, because the platform may not draw it until
              the trader already knows to scroll. Same line the popover heads
              its list with, for the same reason. */}
          <div
            className="text-muted-foreground w-full whitespace-nowrap px-0.5 text-right text-[9px] font-semibold uppercase leading-[14px] tracking-wider"
            data-testid="resting-order-count"
          >
            {orders.length} resting{priceLabel ? ` @ ${priceLabel}` : ''}
          </div>
          <div
            className={cn(
              'flex w-full flex-wrap items-center justify-end gap-x-1.5 gap-y-0.5',
              // The scroll is the height bound doing its job. `overflow-x`
              // stays hidden: a horizontal scroller here would be the old
              // unbounded row with a scrollbar, which is not one of the two
              // choices.
              'min-h-0 flex-1 overflow-y-auto overflow-x-hidden',
            )}
          >
            {ranked.map((o) => (
              <RestingOrderChip key={o.orderId} order={o} />
            ))}
          </div>
        </div>
      </>
    );
  }

  // V3 (exhibit request): the ROW reads right-to-left by fill priority — the
  // nearest-front order holds the right edge, "next to be eaten", and later
  // orders extend left with the +N disclosure at the far left. Same ranked
  // list, reversed only for this one presentation; the panel and the
  // expanded box keep their top-down nearest-first reading.
  const inlineRow = [...inline].reverse();

  return (
    <>
      {level && <CancelLevelControl level={level} orders={orders} compact={compactLevelCancel} />}
      {hiddenCount > 0 && (
        <span className="relative shrink-0">
          <button
            ref={triggerRef}
            type="button"
            // The count IS the disclosure: the contract forbids hiding the
            // existence of further orders without a visible count.
            aria-label={
              `Show all ${orders.length} of your resting orders` +
              `${priceLabel ? ` at ${priceLabel}` : ' at this level'}`
            }
            aria-expanded={open}
            aria-haspopup="dialog"
            title={
              `${hiddenCount} more resting ${hiddenCount === 1 ? 'order' : 'orders'}` +
              `${priceLabel ? ` at ${priceLabel}` : ' at this level'}` +
              ` — click to show all ${orders.length}${stateNote}${queueNote}`
            }
            onClick={(e) => {
              e.stopPropagation();
              if (open) close();
              else setOpen(true);
            }}
            className={cn(
              'text-muted-foreground hover:text-foreground shrink-0 rounded',
              // #1070: the fixed-width gutter needs this padding for 3-digit
              // counts; a pseudo-element keeps the original hit area.
              compactLevelCancel
                ? "relative px-0 before:absolute before:inset-y-0 before:-inset-x-0.5 before:content-['']"
                : 'px-0.5',
              // Same pinned leading as the chips beside it — a disclosure that
              // reintroduced the 4px would undo the fix it belongs to.
              'tabular-nums leading-4 transition-colors',
              'focus-visible:ring-primary/70 focus-visible:outline-none focus-visible:ring-2',
              open && 'bg-muted/60 text-foreground',
              // A hidden order's lifecycle state has nowhere else to show.
              hiddenTimedOut && 'text-amber-400',
              !hiddenTimedOut && hiddenPending && 'animate-pulse opacity-60',
            )}
          >
            +{hiddenCount}
          </button>
          {open &&
            createPortal(
              <div
                ref={panelRef}
                // Non-modal on purpose: `Lt2HotkeyListener`'s MODAL_SELECTOR is
                // `[role="dialog"][aria-modal="true"]`, so a modal here would
                // suppress the A/D hotkeys page-wide the way the pre-#609 bare
                // `[role="dialog"]` query did. Stated explicitly rather than
                // left absent, because the portal puts this on <body> where a
                // future modal query is likelier to sweep it up.
                role="dialog"
                aria-modal="false"
                aria-label={
                  `Your ${orders.length} resting orders` +
                  `${priceLabel ? ` at ${priceLabel}` : ' at this level'}` +
                  ', nearest the front of the queue first'
                }
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                style={{ top: pos.top, left: pos.left, maxHeight: pos.maxHeight, width: PANEL_WIDTH }}
                className={cn(
                  'bg-card border-border fixed z-[100] overflow-y-auto rounded-lg border',
                  'flex flex-col gap-0.5 p-1.5 shadow-lg',
                  'text-[10px] font-normal leading-4 focus:outline-none',
                )}
                // Width is fixed so `reposition` can place it without measuring.
                // max-w keeps it honest on a viewport narrower than the panel.
              >
                {/* The heading carries the TOTAL, so the complete set is legible
                    from the panel even though the panel only draws the part the
                    row could not. */}
                <div className="text-muted-foreground mb-1 whitespace-nowrap px-0.5 text-[9px] font-semibold uppercase tracking-wider">
                  {orders.length} resting{priceLabel ? ` @ ${priceLabel}` : ''}
                </div>
                {/* Only the overflow, NOT the whole list. Re-drawing the inline
                    chips here would put two live controls on screen for one
                    order — and because each chip owns its own arm state, arming
                    one would leave its twin looking un-armed. */}
                {hidden.map((o) => (
                  <span key={o.orderId} className="flex items-center justify-end">
                    <RestingOrderChip order={o} />
                  </span>
                ))}
              </div>,
              document.body,
            )}
        </span>
      )}
      {inlineRow.map((o, i) => (
        <span key={o.orderId} className="contents">
          {(i > 0 || hiddenCount > 0) && <ChipSeparator />}
          {chipSlotPx ? (
            <span
              data-chip-slot
              className="flex min-w-0 overflow-x-clip"
              style={{ flex: `0 1 ${chipSlotPx}px`, justifyContent: 'safe flex-end' }}
            >
              <RestingOrderChip order={o} ellipsis={false} />
            </span>
          ) : (
            <RestingOrderChip order={o} />
          )}
        </span>
      ))}
    </>
  );
}
